/**
 * <sunday-cart-page>: the cart page's behaviour. The page itself is rendered by Liquid
 * (sections/sunday-cart.liquid); after every cart call we ask Shopify for the section again
 * (Section Rendering API, `sections` on the cart call) and swap the [data-r] parts.
 *
 * Every cart call is a POST: on the theme preview GET /cart.js is redirected away, POST is not.
 * Changes are announced as `sunday:cart` so the header count follows.
 * Mockup v88 (layout); v84: sizes in words with "Change", Finish the bed (protector, pillows), the (i) bubbles, the
 * delivery day by Checkout once a pincode is known, and the phone checkout bar while Checkout is out of view.
 * 15 Oct (AR): a bed's room name can be renamed in place (pencil); old-mattress pickup shows for home-city pincodes only;
 * a mattress that came in without a bed gets its bed key written on its lines the first time it is renamed or added to.
 * 16 Oct: Finish the bed adds a topper too (data-add-topper); the Add handler waits for the page to repaint.
 */
import * as state from '@sunday/state';
import { spineFit, fitColour } from '@sunday/sleep';
import { t as T } from '@sunday/text';
import { esc, money as rupees } from '@sunday/util';

const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/* A shared cart link may carry only these line properties (anything else is dropped). */
const SHARE_KEYS = ['_bed', '_bed_label', 'Custom Size'];

let toastTimer = null;
function toast(text, undo) {
  let t = document.querySelector('.sd-toast');
  if (!t) {
    t = document.createElement('div');
    t.className = 'sd-toast';
    t.setAttribute('role', 'status');
    document.body.append(t);
  }
  t.innerHTML = `<span>${esc(text)}</span>${undo ? `<button type="button" class="sd-undo">${esc(T('common.undo'))}</button>` : ''}`;
  t.hidden = false;
  if (undo) t.querySelector('.sd-undo').onclick = () => { t.hidden = true; undo(); };
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => (t.hidden = true), 5000);
}

class SundayCartPage extends HTMLElement {
  connectedCallback() {
    this.section = this.dataset.section;
    this.addEventListener('click', (e) => this.onClick(e));
    this.addEventListener('change', (e) => this.onChange(e));
    document.addEventListener('sunday:state', (e) => { if (e.detail.key === 'pincode') this.paintPin(); });
    document.addEventListener('click', (e) => { if (!e.target.closest('.sd-ci-i, .sd-cpop')) this.pop(null); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.pop(null); });
    this.watchEta();
    this.paint();
    this.shareIn();
    this.saveLang();
  }

  /* Saved on the order for the team (Additional details): Language = the site language the buyer is reading
     (English name, from the header), Journey = the pages they saw, from localStorage['sunday-journey']
     (assets/sunday-header.js), short: "Home › Mattresses › OP › Cart". A new visit after 30 minutes or more
     starts after "‖ +2d ‖" (+45m, +3h, +2d). Only these two lines go to Shopify's own cart; on Checkout the
     form carries them again, ending "› Checkout". */
  journey(end) {
    let j;
    try { j = JSON.parse(localStorage.getItem('sunday-journey') || 'null'); } catch (e) { return null; }
    if (!j || !Array.isArray(j.s) || !j.s.length) return null;
    const out = [];
    let prev = null;
    for (const x of j.s) {
      if (prev && x.t - prev.t >= 18e5) {
        const m = Math.round((x.t - prev.t) / 6e4);
        out.push('‖ +' + (m < 60 ? m + 'm' : m < 1440 ? Math.round(m / 60) + 'h' : Math.round(m / 1440) + 'd') + ' ‖');
      }
      const lang = (!prev && x.c && !/^en/.test(x.c)) || (prev && x.l !== prev.l) ? '[' + x.l + '] ' : '';
      out.push(lang + x.p);
      prev = x;
    }
    if (end) out.push(end);
    let line = out.join(' › ').replace(/ › ‖/g, ' ‖').replace(/‖ › /g, '‖ ');
    if (line.length > 600) line = '… ' + line.slice(line.length - 598).replace(/^[^›‖]*[›‖]\s*/, '');
    return line;
  }

  keepSent(token) {
    try {
      const j = JSON.parse(localStorage.getItem('sunday-journey') || 'null');
      if (!j) return;
      j.sent = String(token || '').split('?')[0];
      j.at = Date.now();
      localStorage.setItem('sunday-journey', JSON.stringify(j));
    } catch (e) { /* storage blocked */ }
  }

  saveLang() {
    const h = document.querySelector('[data-sd-step]');
    const name = (h && h.dataset.sdLang) || this.dataset.lang;
    if (this.classList.contains('sd-cart0')) return;
    const attrs = { Language: name };
    const line = this.journey();
    if (line) attrs.Journey = line;
    fetch(root() + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes: attrs }) })
      .then((r) => r.json())
      .then((c) => this.keepSent(c && c.token))
      .catch(() => {});
    if (this.dataset.notesOn) return;
    this.dataset.notesOn = '1';
    /* Checkout (the page's button and the phone bar both submit this form): add "› Checkout" */
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form || form.id !== 'SundayCartForm') return;
      const full = this.journey((h && h.dataset.sdJco) || '');
      if (!full) return;
      let i = form.querySelector('input[name="attributes[Journey]"]');
      if (!i) { i = document.createElement('input'); i.type = 'hidden'; i.name = 'attributes[Journey]'; form.append(i); }
      i.value = full;
      this.keepSent(decodeURIComponent((document.cookie.match(/(?:^|; )cart=([^;]*)/) || [])[1] || ''));
    }, true);
  }

  /* ---------- talking to Shopify ---------- */
  async call(path, body = {}) {
    const res = await fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: this.section, sections_url: location.pathname }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || T('common.failed'));
    return data;
  }

  /** Swap the changed parts in from the freshly rendered section, then tell the header. */
  async apply(data) {
    let html = data.sections && data.sections[this.section];
    let cart = data.items && 'item_count' in data ? data : null;
    if (!cart) cart = await this.call('cart/update.js', { updates: {} });
    if (!html) html = cart.sections && cart.sections[this.section];
    if (!html) {
      // The theme preview renders cart-call sections with the live theme; ask this page's URL instead.
      const u = new URL(location.href);
      u.searchParams.set('sections', this.section);
      const j = await fetch(u.toString()).then((r) => r.json());
      html = j[this.section];
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector('sunday-cart-page');
    const wasEmpty = !this.querySelector('[data-r="lines"]');
    const nowEmpty = !next.querySelector('[data-r="lines"]');
    this.pop(null);
    if (wasEmpty !== nowEmpty) {
      this.innerHTML = next.innerHTML;
      this.className = next.className;
      this.watchEta();
    } else {
      for (const part of ['count', 'lines', 'fin', 'sum']) {
        const a = this.querySelector(`[data-r="${part}"]`);
        const b = next.querySelector(`[data-r="${part}"]`);
        if (a && b) a.replaceWith(b);
      }
      const t = this.querySelector('[data-bar-total]');
      const nt = next.querySelector('[data-bar-total]');
      if (t && nt) t.textContent = nt.textContent;
    }
    this.paint();
    this.paintDock(cart);
    document.dispatchEvent(new CustomEvent('sunday:cart', { detail: { cart } }));
  }

  async run(button, work) {
    if (button) button.disabled = true;
    this.setAttribute('aria-busy', 'true');
    try {
      await work();
    } catch (err) {
      toast(err.message || T('common.failed'));
      if (button) button.disabled = false;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  lineOf(el) {
    const card = el.closest('[data-line]');
    if (!card) return null;
    let props = {};
    try {
      // Liquid's json filter writes line properties as [key, value] pairs.
      const raw = JSON.parse(card.querySelector('[data-props]').textContent);
      props = Array.isArray(raw) ? Object.fromEntries(raw) : raw || {};
    } catch (e) {
      props = {};
    }
    return { key: card.dataset.line, variant: Number(card.dataset.variant), qty: Number(card.dataset.qty), name: card.dataset.name, props };
  }

  /** True when some line shown in this bed has no `_bed` yet (it came in without one; Liquid gave it this bed). */
  needsStamp(box) {
    if (!box || box.dataset.bed === 'extras') return false;
    return [...box.querySelectorAll('[data-line]')].some((c) => !this.lineOf(c).props._bed);
  }

  /**
   * Write this bed's key (and, when given, its room name) on every line shown in it. Lines keep their place
   * when only properties change, so the cart is walked by position. Returns the last cart call, or null.
   */
  async stampBed(box, label) {
    const bed = box.dataset.bed;
    const keys = new Set([...box.querySelectorAll('[data-line]')].map((c) => c.dataset.line));
    let cart = await this.call('cart/update.js', { updates: {} });
    const start = cart.items.map((it) => it.key);
    let last = null;
    for (let i = 0; i < start.length; i++) {
      if (!keys.has(start[i])) continue;
      const it = cart.items[i];
      if (!it) continue;
      const p = it.properties || {};
      const want = { ...p, _bed: p._bed || bed };
      if (label) want._bed_label = label;
      if (want._bed === p._bed && want._bed_label === p._bed_label) continue;
      last = await this.call('cart/change.js', { line: i + 1, quantity: it.quantity, properties: want });
      cart = last;
    }
    return last;
  }

  /* ---------- clicks ---------- */
  onClick(e) {
    const b = e.target.closest('button');
    if (!b || !this.contains(b)) return;

    if (b.hasAttribute('data-qty-step')) {
      const l = this.lineOf(b);
      const q = Math.max(1, l.qty + Number(b.dataset.qtyStep));
      return this.run(b, async () => this.apply(await this.call('cart/change.js', { id: l.key, quantity: q })));
    }

    if (b.hasAttribute('data-remove')) {
      const l = this.lineOf(b);
      return this.run(b, async () => {
        await this.apply(await this.call('cart/change.js', { id: l.key, quantity: 0 }));
        toast(T('common.removed', { name: l.name }), () => this.restore([l]));
      });
    }

    if (b.hasAttribute('data-remove-bed')) {
      const bed = b.closest('[data-bed]');
      const lines = [...bed.querySelectorAll('[data-line]')].map((c) => this.lineOf(c));
      return this.run(b, async () => {
        await this.apply(await this.call('cart/update.js', { updates: Object.fromEntries(lines.map((l) => [l.key, 0])) }));
        toast(T('common.removed', { name: bed.dataset.label }), () => this.restore(lines));
      });
    }

    if (b.hasAttribute('data-rename-bed')) return this.renameBed(b);

    if (b.hasAttribute('data-add-protector') || b.hasAttribute('data-add-pillow') || b.hasAttribute('data-add-topper')) {
      const box = b.closest('[data-bed]');
      const bed = box.dataset.bed;
      const props = bed === 'extras' ? {} : { _bed: bed };
      const label = (box.dataset.label || '').trim();
      if (label && bed !== 'extras') props._bed_label = label;
      let item;
      if (b.hasAttribute('data-add-protector')) item = { id: Number(b.dataset.addProtector), quantity: 1, properties: props };
      else if (b.hasAttribute('data-add-topper')) item = { id: Number(b.dataset.addTopper), quantity: 1, properties: props };
      else item = { id: Number(b.dataset.addPillow), quantity: Number(b.dataset.n) || 1, properties: props };
      return this.run(b, async () => {
        // A mattress that came in without a bed gets this bed's key first, so it stays with what is added to it.
        if (this.needsStamp(box)) await this.stampBed(box, label && bed !== 'extras' ? label : '');
        await this.apply(await this.call('cart/add.js', { items: [item] }));
      });
    }

    if (b.hasAttribute('data-chg')) {
      const card = b.closest('[data-line]');
      const open = b.getAttribute('aria-expanded') !== 'true';
      b.setAttribute('aria-expanded', String(open));
      b.textContent = open ? b.dataset.done : b.dataset.change;
      const sel = card.querySelector('.sd-csel');
      if (sel) {
        sel.hidden = !open;
        if (open) sel.querySelector('select').focus({ preventScroll: true });
      }
      return;
    }

    if (b.classList.contains('sd-ci-i')) {
      e.preventDefault();
      return this.pop(b.getAttribute('aria-expanded') === 'true' ? null : b);
    }

    if (b.hasAttribute('data-bedpick-toggle')) {
      const open = b.getAttribute('aria-expanded') !== 'true';
      b.setAttribute('aria-expanded', String(open));
      b.nextElementSibling.hidden = !open;
      return;
    }

    if (b.hasAttribute('data-access')) {
      const v = b.dataset.access;
      b.parentElement.querySelectorAll('[data-access]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      return this.run(null, async () => this.apply(await this.call('cart/update.js', { attributes: { 'Delivery access': v } })));
    }

    if (b.hasAttribute('data-share')) return this.share();
    if (b.hasAttribute('data-share-add')) return this.run(b, () => this.shareAdd());
  }

  /** The room name ("Our room", "Bed 2") turns into a field: Enter or tapping away saves it on every line of that bed; Escape keeps the old name. */
  renameBed(b) {
    const box = b.closest('[data-bed]');
    const h = b.closest('.sd-roomh');
    const t = h && h.querySelector('[data-roomt]');
    if (!box || !t || h.querySelector('input')) return;
    const old = (box.dataset.label || t.textContent).trim();
    const inp = document.createElement('input');
    inp.type = 'text';
    inp.className = 'sd-roomin';
    inp.value = old;
    inp.maxLength = 30;
    inp.enterKeyHint = 'done';
    inp.setAttribute('aria-label', b.getAttribute('aria-label') || '');
    t.hidden = true;
    b.hidden = true;
    h.prepend(inp);
    inp.focus({ preventScroll: true });
    inp.select();
    let done = false;
    const finish = (save) => {
      if (done) return;
      done = true;
      const v = inp.value.replace(/\s+/g, ' ').trim();
      inp.remove();
      t.hidden = false;
      b.hidden = false;
      if (!save || !v || v === old) {
        b.focus({ preventScroll: true });
        return;
      }
      t.textContent = v;
      box.dataset.label = v;
      this.run(null, async () => {
        const last = await this.stampBed(box, v);
        if (last) await this.apply(last);
      });
    };
    inp.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); finish(true); }
      else if (e.key === 'Escape') { e.stopPropagation(); finish(false); }
    });
    inp.addEventListener('blur', () => finish(true));
  }

  onChange(e) {
    const t = e.target;
    if (t.matches('[data-size]')) {
      if (!t.value) return;
      const l = this.lineOf(t);
      const props = { ...l.props };
      delete props['Custom Size'];
      // A line can't change variant in place: add the new size with the same bed, then drop the old line.
      return this.run(t, async () => {
        await this.call('cart/add.js', { items: [{ id: Number(t.value), quantity: l.qty, properties: props }] });
        await this.apply(await this.call('cart/change.js', { id: l.key, quantity: 0 }));
      });
    }
    if (t.matches('[data-take-old]')) {
      return this.run(null, async () => this.apply(await this.call('cart/update.js', { attributes: { 'Take away old mattress': t.checked ? 'Yes' : '' } })));
    }
  }

  async restore(lines) {
    await this.run(null, async () => this.apply(await this.call('cart/add.js', {
      items: lines.map((l) => ({ id: l.variant, quantity: l.qty, properties: l.props })),
    })));
  }

  /* ---------- painting what Liquid can't know ---------- */
  paint() {
    this.paintFit();
    this.paintPin();
    this.paintEta();
    this.watchBar();
  }

  /* ---------- the (i) bubbles: one open at a time, under its button ---------- */
  pop(btn) {
    this.querySelectorAll('.sd-cpop').forEach((p) => p.remove());
    this.querySelectorAll('.sd-ci-i[aria-expanded="true"]').forEach((x) => x.setAttribute('aria-expanded', 'false'));
    if (!btn) return;
    const host = btn.closest('.sd-sum') || this;
    const d = document.createElement('div');
    d.className = 'sd-cpop';
    d.id = 'sd-cpop';
    d.setAttribute('role', 'tooltip');
    d.textContent = btn.dataset.tip || '';
    host.append(d);
    btn.setAttribute('aria-expanded', 'true');
    btn.setAttribute('aria-describedby', 'sd-cpop');
    const br = btn.getBoundingClientRect();
    const hr = host.getBoundingClientRect();
    const w = Math.min(260, hr.width - 24);
    d.style.width = w + 'px';
    let left = br.left + br.width / 2 - hr.left - w / 2;
    left = Math.max(12, Math.min(left, hr.width - w - 12));
    d.style.left = left + 'px';
    d.style.top = br.bottom - hr.top + 6 + 'px';
    d.style.setProperty('--ax', br.left + br.width / 2 - hr.left - left + 'px');
  }

  /* ---------- "Delivered by <day> · pincode, city" above Checkout, from the delivery check ---------- */
  watchEta() {
    const del = this.querySelector('.sd-cdate sunday-delivery');
    if (!del || del.__sdEta) return;
    del.__sdEta = true;
    const out = del.querySelector('[data-out]');
    if (out) new MutationObserver(() => this.paintEta()).observe(out, { childList: true, subtree: true });
  }

  paintEta() {
    const p = this.querySelector('[data-eta]');
    if (!p) return;
    const del = this.querySelector('.sd-cdate sunday-delivery');
    const r = del && del.result;
    const pin = state.get('pincode') || '';
    if (!r || !r.to || !/^[1-9]\d{5}$/.test(pin)) {
      p.hidden = true;
      return;
    }
    const extra = del.custom ? 1 : 0;
    const to = new Date(r.to);
    to.setDate(to.getDate() + extra);
    const now = new Date();
    const today = to.toDateString() === now.toDateString();
    const lang = (document.documentElement.lang || 'en').toLowerCase();
    const en = lang.startsWith('en');
    const f = new Intl.DateTimeFormat(en ? 'en-US' : `${lang}-IN`, { weekday: 'short', day: 'numeric', month: 'short' });
    let day = f.format(to);
    if (en) {
      const x = Object.fromEntries(f.formatToParts(to).map((y) => [y.type, y.value]));
      day = `${x.weekday} ${x.day} ${x.month}`;
    }
    const start = this.dataset.localPin || '';
    const city = start && pin.startsWith(start) ? this.dataset.localCity || '' : '';
    const head = today ? p.dataset.today : `${p.dataset.by} ${day}`;
    p.querySelector('span').innerHTML = `<b>${esc(head)}</b> · ${esc(pin)}${city ? ', ' + esc(city) : ''}`;
    p.hidden = false;
  }

  /* ---------- phone and tablet (to 959 px): the page's own checkout bar, from load, whenever the page's Checkout is out of view ---------- */
  watchBar() {
    const set = (on) => {
      const bar = this.querySelector('[data-bar]');
      if (!bar) return;
      bar.classList.toggle('sd-on', on);
      bar.setAttribute('aria-hidden', String(!on));
      const btn = bar.querySelector('button');
      if (btn) btn.tabIndex = on ? 0 : -1;
    };
    const check = () => {
      const own = this.querySelector('#SundayCartForm [name="checkout"]');
      if (!own) return set(false);
      const r = own.getBoundingClientRect();
      // From load, but never while any part of the page's own Checkout shows (it would sit under the bar).
      set(!(r.bottom > 0 && r.top < window.innerHeight));
    };
    if (!this.barWatch) {
      this.barWatch = () => check();
      window.addEventListener('scroll', this.barWatch, { passive: true });
      window.addEventListener('resize', this.barWatch);
      // from 720 px the page scrolls inside .page-wrapper, not the window
      document.querySelector('.page-wrapper')?.addEventListener('scroll', this.barWatch, { passive: true });
    }
    check();
  }

  /** Spine fit on mattresses that came from Build your bed, for the people that bed was built for. */
  paintFit() {
    const lines = this.querySelectorAll('[data-fit]');
    if (!lines.length) return;
    let mats = [];
    try { mats = JSON.parse(this.querySelector('[data-mattresses]').textContent); } catch (e) { return; }
    const beds = state.get('beds') || {};
    lines.forEach((p) => {
      const mat = mats.find((m) => String(m.productId) === p.dataset.fit);
      const bed = p.closest('[data-bed]').dataset.bed;
      const people = (beds[bed] && beds[bed].people) || [state.person()];
      if (!mat || !people.length) return;
      const s = spineFit(mat, people, false);
      p.style.color = fitColour(s);
      p.textContent = (s >= 78 ? '✓ ' : '') + T(people.length > 1 ? 'cart.spine_fit_both' : 'cart.spine_fit_you', { s });
      p.hidden = false;
    });
  }

  /** Old-mattress pick-up and pay on delivery: home city pincodes only (store facts: local_pin_start). Hidden until a home-city pincode is known. */
  paintPin() {
    const pin = state.get('pincode') || '';
    const known = /^[1-9]\d{5}$/.test(pin);
    const start = this.dataset.localPin || '';
    const blr = known && start !== '' && pin.startsWith(start);
    const old = this.querySelector('[data-old]');
    if (old) {
      old.hidden = !blr;
      const cb = old.querySelector('[data-take-old]');
      if (old.hidden && cb && cb.checked) {
        cb.checked = false;
        cb.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const note = old.querySelector('[data-old-note]');
      if (note) note.textContent = blr ? note.dataset.local : note.dataset.other;
    }
    const cod = this.querySelector('[data-cod]');
    if (cod) cod.hidden = !blr;
  }

  paintDock(cart) {
    const dock = document.querySelector('[data-dock]');
    if (!dock || !cart) return;
    const p = dock.querySelector('[data-dock-price]');
    if (p) p.textContent = rupees(cart.total_price);
    if (!cart.item_count) location.reload();
  }

  /* ---------- "Send this cart to family on WhatsApp" ---------- */
  share() {
    const items = [...this.querySelectorAll('[data-line]')].map((c) => {
      const l = this.lineOf(c);
      return [l.variant, l.qty, l.props];
    });
    const code = btoa(unescape(encodeURIComponent(JSON.stringify(items)))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    const url = `${location.origin}${root()}cart?sd_cart=${code}`;
    const total = this.querySelector('[data-total]')?.textContent || '';
    const text = T('cart.share_text', { total, url });
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
  }

  shareCode() {
    const code = new URLSearchParams(location.search).get('sd_cart');
    if (!code) return null;
    try {
      const json = decodeURIComponent(escape(atob(code.replace(/-/g, '+').replace(/_/g, '/'))));
      const items = JSON.parse(json);
      return Array.isArray(items) ? items.filter((x) => Array.isArray(x) && x[0]) : null;
    } catch (e) {
      return null;
    }
  }

  shareIn() {
    const items = this.shareCode();
    const box = this.querySelector('[data-share-in]');
    if (!items || !items.length || !box) return;
    const n = items.reduce((a, x) => a + (Number(x[1]) || 1), 0);
    box.className = 'sd-card sd-sharein';
    box.innerHTML = `<p class="sd-sm" style="margin:0 0 10px"><b>${esc(T('cart.share_in_h'))}</b> ${esc(T.n('cart.share_in', n))}</p><button type="button" class="sd-btn sd-nv sd-full" data-share-add>${esc(T('cart.share_add'))}</button>`;
    box.hidden = false;
  }

  async shareAdd() {
    const items = this.shareCode() || [];
    // Only the bed, its room name and a custom size come across from a shared link; any other property is dropped.
    const clean = (p) => Object.fromEntries(Object.entries(p && typeof p === 'object' ? p : {})
      .filter(([k, v]) => SHARE_KEYS.includes(k) && v != null && typeof v !== 'object')
      .map(([k, v]) => [k, String(v).slice(0, 60)]));
    await this.call('cart/add.js', { items: items.map(([id, q, p]) => ({ id: Number(id), quantity: Math.min(9, Math.max(1, Number(q) || 1)), properties: clean(p) })) });
    const u = new URL(location.href);
    u.searchParams.delete('sd_cart');
    location.replace(u.toString());
  }
}

if (!customElements.get('sunday-cart-page')) customElements.define('sunday-cart-page', SundayCartPage);
